/**
 * Imperative dialog APIs — Promise-based wrappers for file/folder picker dialogs.
 *
 * These allow non-component code (stores, commands) to `await` a dialog result
 * without needing direct access to React state:
 *
 *   const path = await showFilePickerDialog({ accept: [".md"] });
 *   const folder = await showFolderPickerDialog();
 *   const savePath = await showSaveDialog({ defaultName: "untitled.md" });
 *
 * Requires `<DialogHost />` to be mounted in the component tree.
 */

import { type ReactNode, useCallback, useEffect, useState } from "react";
import type { RecentItem } from "../../store/recent-store";
import { frontendLog } from "../../utils/frontend-logger";
import { ConfirmDialog, type ConfirmDialogDetailItem } from "./ConfirmDialog";
import { FilePickerDialog } from "./FilePickerDialog";
import { FolderPickerDialog } from "./FolderPickerDialog";
import { SaveDialog } from "./SaveDialog";

/* ── Request types ── */

interface FilePickerRequest {
    type: "file";
    accept?: string[];
    title?: string;
    initialPath?: string;
    onOpenRecentWorkspace?: (item: RecentItem) => void | Promise<void>;
    resolve: (path: string | null) => void;
}

interface FolderPickerRequest {
    type: "folder";
    title?: string;
    initialPath?: string;
    onOpenRecentWorkspace?: (item: RecentItem) => void | Promise<void>;
    resolve: (path: string | null) => void;
}

interface SaveRequest {
    type: "save";
    title?: string;
    defaultName?: string;
    accept?: string[];
    initialPath?: string;
    resolve: (path: string | null) => void;
}

interface ConfirmRequest {
    type: "confirm";
    title?: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    destructive?: boolean;
    detailItems?: ConfirmDialogDetailItem[];
    footerNote?: string;
    resolve: (confirmed: boolean) => void;
}

type DialogRequest = FilePickerRequest | FolderPickerRequest | SaveRequest | ConfirmRequest;

/* ── Global queue ── */

let _listener: ((req: DialogRequest) => void) | null = null;

function enqueue(req: DialogRequest) {
    if (_listener) {
        _listener(req);
    } else {
        // No DialogHost mounted — resolve with default immediately
        frontendLog.warn("[dialog-host] No DialogHost mounted. Returning default.", undefined, { source: "ui" });
        if (req.type === "confirm") {
            req.resolve(false);
        } else {
            req.resolve(null);
        }
    }
}

/* ── Public imperative APIs ── */

export interface ShowFilePickerOptions {
    accept?: string[];
    title?: string;
    initialPath?: string;
    onOpenRecentWorkspace?: (item: RecentItem) => void | Promise<void>;
}

export function showFilePickerDialog(
    options: ShowFilePickerOptions = {},
): Promise<string | null> {
    return new Promise((resolve) => {
        enqueue({ type: "file", ...options, resolve });
    });
}

export interface ShowFolderPickerOptions {
    title?: string;
    initialPath?: string;
    onOpenRecentWorkspace?: (item: RecentItem) => void | Promise<void>;
}

export function showFolderPickerDialog(
    options: ShowFolderPickerOptions = {},
): Promise<string | null> {
    return new Promise((resolve) => {
        enqueue({ type: "folder", ...options, resolve });
    });
}

export interface ShowSaveDialogOptions {
    title?: string;
    defaultName?: string;
    accept?: string[];
    initialPath?: string;
}

export function showSaveDialog(
    options: ShowSaveDialogOptions = {},
): Promise<string | null> {
    return new Promise((resolve) => {
        enqueue({ type: "save", ...options, resolve });
    });
}

export interface ShowConfirmDialogOptions {
    /** Dialog title (default: "确认") */
    title?: string;
    /** Confirm button label (default: "确认") */
    confirmLabel?: string;
    /** Cancel button label (default: "取消") */
    cancelLabel?: string;
    /** Whether the action is destructive (shows warning style) */
    destructive?: boolean;
    /** Structured detail rows rendered below the message. */
    detailItems?: ConfirmDialogDetailItem[];
    /** Optional muted note below detail rows. */
    footerNote?: string;
}

/**
 * Show a confirmation dialog and return whether the user confirmed.
 *
 * ```ts
 * const ok = await showConfirmDialog("Are you sure?");
 * if (ok) { doSomething(); }
 * ```
 */
export function showConfirmDialog(
    message: string,
    options: ShowConfirmDialogOptions = {},
): Promise<boolean> {
    return new Promise((resolve) => {
        enqueue({ type: "confirm", message, ...options, resolve });
    });
}

/* ── DialogHost component ── */

/**
 * Render this component once near the root of your app.
 * It listens for imperative dialog requests and renders the appropriate dialog.
 *
 * ```tsx
 * function App() {
 *     return (
 *         <>
 *             <MainContent />
 *             <DialogHost />
 *         </>
 *     );
 * }
 * ```
 */
export function DialogHost(): ReactNode {
    const [request, setRequest] = useState<DialogRequest | null>(null);

    useEffect(() => {
        _listener = (req) => setRequest(req);
        return () => {
            _listener = null;
        };
    }, []);

    const handleDone = useCallback(
        (result: string | boolean | null) => {
            if (request?.type === "confirm") {
                (request as ConfirmRequest).resolve(result as boolean);
            } else {
                (request as FilePickerRequest | FolderPickerRequest | SaveRequest).resolve(result as string | null);
            }
            setRequest(null);
        },
        [request],
    );

    if (!request) return null;

    switch (request.type) {
        case "file":
            return (
                <FilePickerDialog
                    open
                    title={request.title}
                    accept={request.accept}
                    initialPath={request.initialPath}
                    onOpenRecentWorkspace={request.onOpenRecentWorkspace}
                    onSelect={(p) => handleDone(p)}
                    onCancel={() => handleDone(null)}
                />
            );
        case "folder":
            return (
                <FolderPickerDialog
                    open
                    title={request.title}
                    initialPath={request.initialPath}
                    onOpenRecentWorkspace={request.onOpenRecentWorkspace}
                    onSelect={(p) => handleDone(p)}
                    onCancel={() => handleDone(null)}
                />
            );
        case "save":
            return (
                <SaveDialog
                    open
                    title={request.title}
                    defaultName={request.defaultName}
                    accept={request.accept}
                    initialPath={request.initialPath}
                    onSave={(p) => handleDone(p)}
                    onCancel={() => handleDone(null)}
                />
            );
        case "confirm":
            return (
                <ConfirmDialog
                    open
                    title={request.title}
                    message={request.message}
                    confirmLabel={request.confirmLabel}
                    cancelLabel={request.cancelLabel}
                    destructive={request.destructive}
                    detailItems={request.detailItems}
                    footerNote={request.footerNote}
                    onConfirm={() => handleDone(true)}
                    onCancel={() => handleDone(false)}
                />
            );
    }
}
